/**
 * The Automations page's pure logic: switching a draft between schedule
 * kinds, deciding whether it can be saved, what its next run will be, where
 * a stored automation sits in the list and what its row says, and how an
 * approved send reads back.
 *
 * Every schedule question goes to `@repo/shared/automationSchedule` — the
 * module the API validates with and the runners advance by — so the preview
 * here cannot promise a time the runner does not keep. For a stored
 * automation the API's `nextRunAt` / `nextRunLabel` / `scheduleLabel` stay
 * the authority; this module only speaks for drafts the API has not seen.
 *
 * @module components/automations/automationDraft
 */
import {
  computeNextRunAt,
  describeSchedule,
  describeTimeUntil,
  formatInTimezone,
  MAX_INTERVAL_MINUTES,
  MIN_INTERVAL_MINUTES,
  normalizeSchedule,
  zonedDateKey,
  type AutomationFrequency,
} from "@repo/shared/automationSchedule";

import type { AutomationSchedule, AutomationView } from "../../state/automations";
import { formatRelativeTimeLabel } from "../../timestampFormat";

export interface Draft {
  readonly title: string;
  readonly prompt: string;
  readonly schedule: AutomationSchedule;
}

export const emptyDraft = (timezone: string): Draft => ({
  title: "",
  prompt: "",
  schedule: { frequency: "daily", time: "09:00", timezone },
});

export const draftFrom = (automation: AutomationView): Draft => ({
  title: automation.title,
  prompt: automation.prompt,
  schedule: automation.schedule,
});

/** The "Repeats" options, in the order the picker lists them. */
export const FREQUENCY_OPTIONS: ReadonlyArray<{
  readonly value: AutomationFrequency;
  readonly label: string;
}> = [
  { value: "once", label: "Once" },
  { value: "interval", label: "Every…" },
  { value: "daily", label: "Daily" },
  { value: "weekdays", label: "Weekdays" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

/** Monday first, as a UK calendar reads; values are 0 = Sunday … 6 = Saturday. */
export const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;

const WORKING_WEEK = [1, 2, 3, 4, 5];
const DAY_MS = 24 * 60 * 60 * 1000;

function addDays(dateKey: string, days: number): string {
  return new Date(Date.parse(`${dateKey}T12:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);
}

/** `computeNextRunAt` without the throw a half-edited schedule can provoke. */
function safeNextRunAt(schedule: AutomationSchedule, after: Date): Date | null {
  try {
    return computeNextRunAt(schedule, after);
  } catch {
    return null;
  }
}

/** The days a weekly rule (or a day-limited interval) runs on; empty = every day. */
export function selectedWeekdays(schedule: AutomationSchedule): ReadonlyArray<number> {
  if (schedule.weekdays && schedule.weekdays.length > 0) return schedule.weekdays;
  if (schedule.frequency === "weekly") return [schedule.weekday ?? 1];
  return [];
}

/**
 * A day chip pressed. A weekly rule keeps at least one day — un-pressing the
 * last is ignored rather than leaving a rule that never runs. For an interval
 * an empty filter means every day, so all seven start pressed and pressing
 * them all again drops the filter.
 */
export function toggleWeekday(schedule: AutomationSchedule, day: number): AutomationSchedule {
  const current = selectedWeekdays(schedule);
  const base = current.length === 0 ? [0, 1, 2, 3, 4, 5, 6] : current;
  const next = base.includes(day)
    ? base.filter((existing) => existing !== day)
    : [...base, day].sort((a, b) => a - b);
  if (next.length === 0) return schedule;
  const { weekday: _legacy, weekdays: _days, ...rest } = schedule;
  if (schedule.frequency === "interval" && next.length === 7) return rest;
  return { ...rest, weekdays: next };
}

/**
 * The schedule switched to another kind. Time and zone carry over; fields the
 * new kind does not read are dropped (so monthly never keeps a stale weekday);
 * fields it does read keep a sensible value from the old kind — chosen days
 * survive weekly ↔ interval, a start/end window survives between recurring
 * kinds, and a one-off lands on the next day its time is still ahead.
 */
export function withFrequency(
  schedule: AutomationSchedule,
  frequency: AutomationFrequency,
  now: Date,
): AutomationSchedule {
  const { time, timezone } = schedule;
  if (frequency === "once") {
    const today = zonedDateKey(now, timezone);
    const future = (date: string) =>
      safeNextRunAt({ frequency: "once", date, time, timezone }, now) !== null;
    const date =
      schedule.date !== undefined && future(schedule.date)
        ? schedule.date
        : future(today)
          ? today
          : addDays(today, 1);
    return { frequency, time, timezone, date };
  }

  const window = {
    ...(schedule.startDate !== undefined ? { startDate: schedule.startDate } : {}),
    ...(schedule.endDate !== undefined ? { endDate: schedule.endDate } : {}),
  };
  const days = selectedWeekdays(schedule);
  switch (frequency) {
    case "interval": {
      const weekdays =
        schedule.frequency === "weekdays" ? [...WORKING_WEEK] : days.length > 0 ? [...days] : null;
      return {
        frequency,
        time,
        timezone,
        everyMinutes: schedule.everyMinutes ?? 60,
        ...(schedule.until !== undefined ? { until: schedule.until } : {}),
        ...(weekdays !== null && weekdays.length < 7 ? { weekdays } : {}),
        ...window,
      };
    }
    case "weekly": {
      const weekdays =
        schedule.frequency === "weekdays" ? [...WORKING_WEEK] : days.length > 0 ? [...days] : [1];
      return { frequency, time, timezone, weekdays, ...window };
    }
    case "monthly":
      return { frequency, time, timezone, dayOfMonth: schedule.dayOfMonth ?? 1, ...window };
    default:
      return { frequency, time, timezone, ...window };
  }
}

export type IntervalUnit = "minutes" | "hours";

/** How the picker first shows an interval: whole hours as hours, else minutes. */
export function intervalUnitOf(everyMinutes: number | undefined): IntervalUnit {
  const minutes = everyMinutes ?? 60;
  return minutes >= 60 && minutes % 60 === 0 ? "hours" : "minutes";
}

/** The "Every" field's number for the chosen unit; NaN while the field is empty. */
export function intervalAmount(everyMinutes: number | undefined, unit: IntervalUnit): number {
  const minutes = everyMinutes ?? 60;
  return unit === "hours" ? minutes / 60 : minutes;
}

/** The field's text and unit as stored minutes; an empty field stays NaN (invalid). */
export function everyMinutesFrom(amount: string, unit: IntervalUnit): number {
  if (amount.trim().length === 0) return Number.NaN;
  const value = Number(amount);
  return unit === "hours" ? Math.round(value * 60) : value;
}

/**
 * The shared validator's sentence, except where it names a stored field the
 * person never sees: an interval out of range says so in the picker's terms.
 */
function scheduleErrorText(schedule: AutomationSchedule, error: string): string {
  if (schedule.frequency === "interval") {
    const every = schedule.everyMinutes;
    if (
      typeof every !== "number" ||
      !Number.isInteger(every) ||
      every < MIN_INTERVAL_MINUTES ||
      every > MAX_INTERVAL_MINUTES
    ) {
      return `Pick an interval from ${MIN_INTERVAL_MINUTES} minutes to ${MAX_INTERVAL_MINUTES / 60} hours.`;
    }
  }
  return error;
}

export type ScheduleCheck =
  | { readonly schedule: AutomationSchedule; readonly nextRunAt: Date; readonly error: null }
  | { readonly schedule: null; readonly nextRunAt: null; readonly error: string };

/**
 * The draft schedule as the API will judge it at `now`: its canonical form and
 * first run, or the one sentence that says why it cannot be saved (a one-off
 * already past, an end date gone, an interval under 15 minutes).
 */
export function checkSchedule(schedule: AutomationSchedule, now: Date): ScheduleCheck {
  const normalized = normalizeSchedule(schedule, now);
  if ("error" in normalized) {
    return {
      schedule: null,
      nextRunAt: null,
      error: scheduleErrorText(schedule, normalized.error),
    };
  }
  const nextRunAt = safeNextRunAt(normalized.schedule, now);
  if (nextRunAt === null) {
    return { schedule: null, nextRunAt: null, error: "This schedule has no run left." };
  }
  return { schedule: normalized.schedule, nextRunAt, error: null };
}

/** The picker's label: the rule in words, or a prompt to finish it. */
export function scheduleLabelOf(schedule: AutomationSchedule): string {
  const normalized = normalizeSchedule(schedule);
  return "error" in normalized ? "Finish the schedule" : describeSchedule(normalized.schedule);
}

/** Key order ignored: the stored copy comes back from jsonb with its keys resorted. */
const canonicalSchedule = (schedule: AutomationSchedule): string =>
  JSON.stringify(
    Object.entries(schedule)
      .filter(([, value]) => value !== undefined)
      .sort(([a], [b]) => a.localeCompare(b)),
  );

export const sameSchedule = (a: AutomationSchedule, b: AutomationSchedule): boolean =>
  canonicalSchedule(a) === canonicalSchedule(b);

/** Whether the panel holds edits the stored automation does not have. */
export const draftDiffers = (draft: Draft, stored: AutomationView): boolean =>
  draft.title !== stored.title ||
  draft.prompt !== stored.prompt ||
  !sameSchedule(draft.schedule, stored.schedule);

/** Sets an optional text field of the schedule, or removes it when cleared. */
export function withOptionalField(
  schedule: AutomationSchedule,
  key: "until" | "startDate" | "endDate",
  value: string,
): AutomationSchedule {
  const next: { -readonly [K in keyof AutomationSchedule]: AutomationSchedule[K] } = {
    ...schedule,
  };
  if (value.trim().length === 0) {
    delete next[key];
  } else {
    next[key] = value;
  }
  return next;
}

/** What pressing Create or Save would send, or why it can't be pressed. */
export interface DraftSubmission {
  /** Create/Save may be pressed. */
  readonly valid: boolean;
  /** The schedule sentence to show under the details; null when there is none. */
  readonly scheduleError: string | null;
  /** The schedule to send — canonical — or null to leave the stored one. */
  readonly schedule: AutomationSchedule | null;
  /** When the draft's schedule would first run; null when unchanged or invalid. */
  readonly nextRunAt: Date | null;
  /** Send `enabled: true`: a finished automation turned back on by a new time. */
  readonly reenable: boolean;
}

/**
 * Whether the draft can be sent, and what. A new automation needs a title,
 * a prompt and a valid schedule. An edit checks the schedule only when it
 * changed — a title fix must not be blocked by a one-off that already ran,
 * nor reschedule a run it did not touch. A scheduled send's title and prompt
 * are fixed, so only its schedule counts.
 */
export function draftSubmission(
  draft: Draft,
  stored: AutomationView | null,
  now: Date,
): DraftSubmission {
  const isAction = stored !== null && stored.action !== null;
  const hasText = isAction || (draft.title.trim().length > 0 && draft.prompt.trim().length > 0);
  const scheduleChanged = stored === null || !sameSchedule(draft.schedule, stored.schedule);
  if (!scheduleChanged) {
    return {
      valid: hasText,
      scheduleError: null,
      schedule: null,
      nextRunAt: null,
      reenable: false,
    };
  }
  const check = checkSchedule(draft.schedule, now);
  return {
    valid: hasText && check.error === null,
    scheduleError: check.error,
    schedule: check.schedule,
    nextRunAt: check.nextRunAt,
    reenable: check.error === null && stored !== null && stored.finished,
  };
}

/**
 * The panel's one line about when it runs next. A draft (new, or with a
 * changed schedule) is previewed on the schedule's own wall clock; a stored
 * automation uses the API's label; a finished one says what to do about it.
 * Null when the draft is invalid — its error says why instead.
 */
export function nextRunPreview(
  draft: Draft,
  stored: AutomationView | null,
  submission: DraftSubmission,
  now: Date,
): string | null {
  const zone = draft.schedule.timezone;
  if (submission.schedule !== null && submission.nextRunAt !== null) {
    const when = formatInTimezone(submission.nextRunAt, zone);
    return stored !== null && !stored.enabled && !submission.reenable
      ? `Paused · when resumed, next run ${when}`
      : `Next run ${when}`;
  }
  if (submission.scheduleError !== null || stored === null) return null;
  if (stored.finished) return "Finished — pick a new time to run it again.";
  const storedNext = new Date(stored.nextRunAt);
  const label = stored.nextRunLabel || formatInTimezone(storedNext, stored.schedule.timezone);
  if (stored.enabled) return `Next run ${label}`;
  // Resuming keeps a next run still ahead and otherwise moves to the rule's
  // next occurrence, as the API does.
  const resumed =
    storedNext.getTime() > now.getTime() ? storedNext : safeNextRunAt(stored.schedule, now);
  return resumed === null
    ? "Paused"
    : `Paused · when resumed, next run ${
        resumed === storedNext ? label : formatInTimezone(resumed, stored.schedule.timezone)
      }`;
}

export type AutomationSection = "current" | "paused" | "done";

/** Running; switched off with runs ahead; or switched off with none left. */
export function sectionOf(
  automation: Pick<AutomationView, "enabled" | "finished">,
): AutomationSection {
  if (automation.enabled) return "current";
  return automation.finished ? "done" : "paused";
}

export function bucketAutomations<T extends Pick<AutomationView, "enabled" | "finished">>(
  automations: ReadonlyArray<T>,
): Readonly<Record<AutomationSection, ReadonlyArray<T>>> {
  const buckets: Record<AutomationSection, T[]> = { current: [], paused: [], done: [] };
  for (const automation of automations) buckets[sectionOf(automation)].push(automation);
  return buckets;
}

/** Which icon a row gets: an approved send, a one-off, or a recurring rule. */
export function rowKindOf(
  automation: Pick<AutomationView, "action" | "schedule">,
): "action" | "once" | "recurring" {
  if (automation.action !== null) return "action";
  return automation.schedule.frequency === "once" ? "once" : "recurring";
}

/** The list row's second line. */
export function statusLine(
  automation: Pick<
    AutomationView,
    "enabled" | "finished" | "lastRunAt" | "nextRunAt" | "scheduleLabel"
  >,
  now: Date,
): string {
  if (automation.enabled) {
    return `Next run ${describeTimeUntil(new Date(automation.nextRunAt), now)} · ${automation.scheduleLabel}`;
  }
  if (!automation.finished) return `Paused · ${automation.scheduleLabel}`;
  const ran = automation.lastRunAt ? formatRelativeTimeLabel(automation.lastRunAt) : "";
  return ran
    ? `Ran ${ran} · ${automation.scheduleLabel}`
    : `Finished · ${automation.scheduleLabel}`;
}

export interface ActionField {
  readonly label: string;
  readonly value: string;
  /** A message body: shown clamped to a few lines. */
  readonly long: boolean;
}

/** Recipients and subject first, then the body, then whatever else was approved. */
const FIELD_ORDER = [
  "to",
  "recipient",
  "recipients",
  "recipient_email",
  "cc",
  "bcc",
  "subject",
  "title",
  "body",
  "message",
  "text",
  "content",
];
const LONG_FIELDS = new Set(["body", "message", "text", "content", "html", "description"]);
const MAX_ACTION_FIELDS = 8;

/** How a person reads the usual email fields; anything else is spelled out. */
const FIELD_LABELS: Readonly<Record<string, string>> = {
  recipient_email: "To",
  recipient: "To",
  to: "To",
  to_email: "To",
  cc: "Cc",
  bcc: "Bcc",
};

/** "recipient_email" → "To", "replyTo" → "Reply to". */
function labelOf(key: string): string {
  const known = FIELD_LABELS[key.toLowerCase()];
  if (known) return known;
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase();
  return words.length === 0 ? key : words[0]!.toUpperCase() + words.slice(1);
}

function textOf(value: unknown): string | null {
  if (typeof value === "string") return value.trim().length > 0 ? value.trim() : null;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    const items = value.filter(
      (item): item is string => typeof item === "string" && item.trim().length > 0,
    );
    return items.length > 0 && items.length === value.length ? items.join(", ") : null;
  }
  return null;
}

/**
 * The approved call's arguments as label/value rows: the readable scalar
 * fields (and lists of strings, such as several recipients), recipients and
 * subject first, capped so an odd tool's many flags don't drown the panel.
 * Nested objects are left out — the summary already says what it does.
 */
export function summariseActionArguments(
  args: Readonly<Record<string, unknown>>,
): ReadonlyArray<ActionField> {
  const rank = (key: string) => {
    const index = FIELD_ORDER.indexOf(key.toLowerCase());
    return index === -1 ? FIELD_ORDER.length : index;
  };
  const fields: ActionField[] = [];
  const keys = Object.keys(args)
    .map((key, position) => ({ key, position }))
    .sort((a, b) => rank(a.key) - rank(b.key) || a.position - b.position);
  for (const { key } of keys) {
    const value = textOf(args[key]);
    if (value === null) continue;
    fields.push({ label: labelOf(key), value, long: LONG_FIELDS.has(key.toLowerCase()) });
    if (fields.length === MAX_ACTION_FIELDS) break;
  }
  return fields;
}

/** "Approved in chat on 25 Sep 2026" — the date only, in the reader's locale zone. */
export function approvedOnLabel(approvedAt: string): string {
  const date = new Date(approvedAt);
  if (Number.isNaN(date.getTime())) return "Approved in chat";
  return `Approved in chat on ${date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })}`;
}
